import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtCurrency, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KpiCard, PageHeader } from "@/components/common/Primitives";
import { SalesFunnelChart } from "@/components/analytics/SalesFunnelChart";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Mail, Target, FileText, ClipboardList, ShoppingCart, DollarSign, TrendingUp, Wallet, Gauge, CalendarClock,
} from "lucide-react";

const RANGES = [
  { v: "this_month", l: "This month" }, { v: "last_month", l: "Last month" },
  { v: "last_3_months", l: "Last 3 months" }, { v: "last_6_months", l: "Last 6 months" },
  { v: "this_year", l: "This year" }, { v: "all", l: "All time" },
];

export default function Dashboard() {
  const { user, company } = useAuth();
  const navigate = useNavigate();
  const [range, setRange] = useState("all");
  const [m, setM] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [fu, setFu] = useState({ today: [], overdue: [], upcoming: [] });

  useEffect(() => {
    api.get(`/dashboard/metrics?range=${range}`).then(({ data }) => setM(data)).catch(() => {});
  }, [range]);
  useEffect(() => {
    api.get("/dashboard/funnel").then(({ data }) => setFunnel(data)).catch(() => {});
    api.get("/followups/summary").then(({ data }) => setFu(data)).catch(() => {});
  }, []);

  const stageRoute = { enquiries: "/leads", qualified: "/leads", rfqs: "/rfqs", quotations: "/quotations", orders: "/orders", visitors: "/analytics" };

  return (
    <div>
      <PageHeader title={`Welcome back, ${user.name.split(" ")[0]}`} subtitle={`${company?.name || "Export"} · Turn international buyer interest into measurable export business`}>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-44 bg-white border-slate-200" data-testid="dashboard-range-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{RANGES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      {m && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard testid="kpi-enquiries-card" label="International Enquiries" value={m.enquiries} icon={Mail} accent="sky" delta={18} />
            <KpiCard testid="kpi-qualified-card" label="Qualified Leads" value={m.qualified} icon={Target} accent="sky" delta={12} />
            <KpiCard testid="kpi-rfqs-card" label="RFQs" value={m.rfqs} icon={FileText} accent="amber" delta={9} />
            <KpiCard testid="kpi-quotations-card" label="Quotations" value={m.quotations} icon={ClipboardList} accent="slate" delta={-4} />
            <KpiCard testid="kpi-orders-card" label="Orders" value={m.orders} icon={ShoppingCart} accent="emerald" delta={25} />
            <KpiCard testid="kpi-revenue-card" label="Export Revenue" value={m.revenue} currency icon={DollarSign} accent="emerald" delta={21} />
            <KpiCard testid="kpi-gross-profit-card" label="Gross Profit" value={m.gross_profit} currency icon={TrendingUp} accent="emerald" delta={19} />
            <KpiCard testid="kpi-platform-card" label="Platform Investment" value={m.platform_cost} currency icon={Wallet} accent="slate" />
          </div>

          <Card className="border border-slate-200/80 rounded-xl shadow-sm p-6 mb-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-xl bg-sky-500/20 flex items-center justify-center"><Gauge className="h-7 w-7 text-sky-400" /></div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-400">Return on Platform Investment</p>
                  <p className="text-4xl font-bold font-display mt-1">{m.roi}×</p>
                </div>
              </div>
              <div className="text-sm text-slate-300 max-w-sm">
                For every ₹1 invested in the platform, your attributed export gross profit returns <span className="text-sky-400 font-semibold">₹{m.roi}</span>. Based on platform-attributed orders.
              </div>
            </div>
          </Card>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-8 border border-slate-200/80 rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-slate-800 font-display mb-1">Export Sales Funnel</h3>
          <p className="text-sm text-slate-500 mb-6">Click any stage to open its records</p>
          {funnel.length > 0 && <SalesFunnelChart stages={funnel} onStageClick={(k) => navigate(stageRoute[k] || "/dashboard")} />}
        </Card>

        <Card className="lg:col-span-4 border border-slate-200/80 rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-slate-800 font-display mb-4 flex items-center gap-2"><CalendarClock className="h-5 w-5 text-slate-400" /> Follow-ups</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-rose-50 border border-rose-100">
              <span className="text-sm text-rose-700 font-medium">Overdue</span>
              <span className="text-lg font-bold text-rose-700 font-display" data-testid="fu-overdue-count">{fu.overdue.length}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-100">
              <span className="text-sm text-amber-700 font-medium">Due today</span>
              <span className="text-lg font-bold text-amber-700 font-display" data-testid="fu-today-count">{fu.today.length}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-sky-50 border border-sky-100">
              <span className="text-sm text-sky-700 font-medium">Upcoming</span>
              <span className="text-lg font-bold text-sky-700 font-display" data-testid="fu-upcoming-count">{fu.upcoming.length}</span>
            </div>
          </div>
          <div className="mt-5 space-y-2 max-h-56 overflow-y-auto">
            {[...fu.overdue, ...fu.today].slice(0, 6).map((f) => (
              <div key={f.id} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2">
                <span className="text-slate-600 truncate">{f.buyer_name || "Buyer"}</span>
                <StatusBadge value={f.status} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
